/* eslint-disable no-shadow */
import React, {
  useState, useEffect, useCallback, useRef,
} from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import cx from 'classnames';
import {
  getAutocompleteSearchThunk,
  getTranslation,
  Icon,
} from '@resy/ui-kitchen';
import Logger from '../../../../../ui-kitchen/src/utils/logger';
import Autocomplete from '../../components/Autocomplete';
import './AutocompleteContainer.scss';
import { ISO_DATE_FORMAT } from '../../../../../ui-kitchen/src/constants/resyCore';

const autocompleteContainerPropTypes = {
  Analytics: PropTypes.shape({
    event: PropTypes.func.isRequired,
  }).isRequired,
  autocompletePlaceholder: PropTypes.string,
  collections: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    type_id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
    isSelected: PropTypes.bool.isRequired,
    urlSlug: PropTypes.string.isRequired,
  })),
  facetValues: PropTypes.arrayOf(PropTypes.shape({})),
  getAutocompleteSearchThunk: PropTypes.func.isRequired,
  hits: PropTypes.arrayOf(PropTypes.shape({})),
  isDropdownOpen: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool.isRequired,
  isHidden: PropTypes.bool.isRequired,
  latLng: PropTypes.shape({
    latitude: PropTypes.number.isRequired,
    longitude: PropTypes.number.isRequired,
  }),
  locationCode: PropTypes.string,
  locationName: PropTypes.string,
  onCollectionSelect: PropTypes.func.isRequired,
  onDropdownOpen: PropTypes.func.isRequired,
  onFacetSelect: PropTypes.func.isRequired,
  onQuery: PropTypes.func.isRequired,
  onSuggestionSelect: PropTypes.func.isRequired,
  selectedCoreISODate: PropTypes.string.isRequired,
  selectedCoreSeats: PropTypes.number.isRequired,
  selectedFacet: PropTypes.string,
  selectedQuery: PropTypes.string,
  shouldResetAutocompleteValue: PropTypes.bool.isRequired,
};

const AutocompleteContainer = ({
  Analytics,
  autocompletePlaceholder,
  collections,
  facetValues,
  hits,
  latLng,
  locationCode,
  locationName,
  onCollectionSelect,
  onDropdownOpen,
  onFacetSelect,
  onQuery,
  onSuggestionSelect,
  selectedCoreISODate,
  selectedCoreSeats,
  selectedFacet,
  selectedQuery,
  shouldResetAutocompleteValue,
  isDropdownOpen,
  isMobile,
  isHidden,
  getAutocompleteSearchThunk,
}) => {
  const [value, setValue] = useState('');
  const searchTimeout = useRef();

  const search = useCallback(
    (searchValue) => {
      if (!searchValue) return;

      const selectedDate = moment(selectedCoreISODate, ISO_DATE_FORMAT).toDate();
      const params = {
        latitude: latLng?.latitude,
        longitude: latLng?.longitude,
        value: searchValue,
        selectedDate,
        selectedPartySize: selectedCoreSeats,
      };

      if (searchTimeout.current) clearTimeout(searchTimeout.current);

      searchTimeout.current = setTimeout(() => {
        getAutocompleteSearchThunk(params).catch((err) => {
          const message = err?.data?.message || err.status;
          Logger.error(`[AutocompleteContainer] search error: ${message}`);
        });
      }, 500);
    },
    [latLng, getAutocompleteSearchThunk, selectedCoreISODate, selectedCoreSeats]
  );

  const resetValue = useCallback(() => {
    setValue('');
  }, []);

  useEffect(() => {
    if (selectedQuery) {
      setValue(selectedQuery);
      search(selectedQuery);
    }
  }, [selectedQuery]);

  useEffect(() => {
    search(value);
  }, [latLng]);

  useEffect(() => {
    if (shouldResetAutocompleteValue) {
      resetValue();
    }
  }, [shouldResetAutocompleteValue]);

  const renderDesktop = () => (
    <Autocomplete
      Analytics={Analytics}
      collections={collections}
      facetValues={facetValues}
      hits={hits}
      isMobile={isMobile}
      isOpen={isDropdownOpen}
      locationCode={locationCode}
      locationName={locationName}
      onAutocompleteOpen={onDropdownOpen}
      onCollectionSelected={onCollectionSelect}
      onFacetSelect={onFacetSelect}
      onQuery={onQuery}
      onResetAutocompleteValue={resetValue}
      onSearchRequested={search}
      onSuggestionSelected={onSuggestionSelect}
      onValueChange={setValue}
      placeholder={autocompletePlaceholder}
      selectedFacet={selectedFacet}
      selectedQuery={selectedQuery}
      value={value}
    />
  );

  const renderMobile = () => (
    <>
      {!isDropdownOpen ? (
        <div
          aria-label={getTranslation({ translationKey: 'search', fallback: 'Search' })}
          className="MagnifyingGlass__nav-click-container"
          id="magnifyingGlassTitle"
          onClick={onDropdownOpen}
          onKeyPress={onDropdownOpen}
          role="button"
          tabIndex="0"
        >
          <Icon name="MagnifyingGlass" />
        </div>
      ) : (
        <Autocomplete
          Analytics={Analytics}
          collections={collections}
          facetValues={facetValues}
          hits={hits}
          isMobile={isMobile}
          isOpen={isDropdownOpen}
          locationCode={locationCode}
          locationName={locationName}
          onAutocompleteOpen={onDropdownOpen}
          onCollectionSelected={onCollectionSelect}
          onFacetSelect={onFacetSelect}
          onQuery={onQuery}
          onResetAutocompleteValue={resetValue}
          onSearchRequested={search}
          onSuggestionSelected={onSuggestionSelect}
          onValueChange={setValue}
          placeholder={autocompletePlaceholder}
          selectedFacet={selectedFacet}
          selectedQuery={selectedQuery}
          value={value}
        />
      )}
    </>
  );

  return (
    <div
      className={cx('AutocompleteContainer', {
        'AutocompleteContainer--mobile': isMobile,
        'AutocompleteContainer--desktop': !isMobile,
        'AutocompleteContainer--dropdown-open': isDropdownOpen,
        'AutocompleteContainer--hidden': isHidden,
      })}
    >
      {isMobile ? renderMobile() : renderDesktop()}
    </div>
  );
};

AutocompleteContainer.propTypes = autocompleteContainerPropTypes;
AutocompleteContainer.defaultProps = {
  autocompletePlaceholder: null,
  collections: undefined,
  facetValues: [],
  hits: [],
  latLng: null,
  locationCode: null,
  locationName: null,
  selectedFacet: '',
  selectedQuery: null,
};
AutocompleteContainer.displayName = 'AutocompleteContainer';

const mapStateToProps = ({
  venueSearch: {
    autocomplete: { facetValues, hits },
  },
  resyCore: { selectedCoreISODate, selectedCoreSeats },
}) => ({
  facetValues,
  hits,
  selectedCoreISODate,
  selectedCoreSeats,
});

const mapDispatchToProps = {
  getAutocompleteSearchThunk,
};

export { AutocompleteContainer as ComponentUnderTest, autocompleteContainerPropTypes };
export default connect(mapStateToProps, mapDispatchToProps)(AutocompleteContainer);
